import { defineStore } from 'pinia';
import { ref } from 'vue';

export type ToastType = 'info' | 'success' | 'warning' | 'error';

export interface ToastAction {
    label: string;
    handler: () => void;
}

export interface Toast {
    id: number;
    message: string;
    type: ToastType;
    action: ToastAction | null;
}

export const useToastStore = defineStore('toast', () => {
    const toasts = ref<Toast[]>([]);
    const timers = new Map<number, ReturnType<typeof setTimeout>>();
    let nextId = 0;

    function add(
        message: string,
        type: ToastType = 'info',
        duration = 3000,
        action: ToastAction | null = null,
    ): number {
        const id = nextId++;
        toasts.value.push({ id, message, type, action });
        if (duration > 0) {
            const timer = setTimeout(() => {
                remove(id);
            }, duration);
            timers.set(id, timer);
        }

        return id;
    }

    function remove(id: number): void {
        const timer = timers.get(id);
        if (timer) {
            clearTimeout(timer);
            timers.delete(id);
        }
        toasts.value = toasts.value.filter((t) => t.id !== id);
    }

    function success(message: string, duration?: number): number {
        return add(message, 'success', duration);
    }

    function info(message: string, duration?: number): number {
        return add(message, 'info', duration);
    }

    function warning(message: string, duration?: number): number {
        return add(message, 'warning', duration);
    }

    function error(message: string, duration?: number, action: ToastAction | null = null): number {
        return add(message, 'error', duration, action);
    }

    return { toasts, add, remove, success, info, warning, error };
});
